import React, { useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import './ClientFeedLookup.css';

/**
 * Trade Watcher → Settings (Support and up): look up a client by email and copy
 * their feed URL, to install the CSA alert-only build on a hands-off client's
 * NinjaTrader. Support only finds clients in their own company; the client's URL
 * is never changed here, so their own NinjaTrader keeps working.
 */
export default function ClientFeedLookup({ headers }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notPro, setNotPro] = useState('');
  const [client, setClient] = useState(null);
  const [copied, setCopied] = useState('');

  const lookUp = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) {
      setError('Enter the client’s email first.');
      return;
    }
    setBusy(true);
    setError('');
    setNotPro('');
    setCopied('');
    setClient(null);
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherClientFeed, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers() },
        body: JSON.stringify({ email: value }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.notPro) {
        setNotPro(data.error);
        return;
      }
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t look up that client');
      setClient(data.client);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const feedUrl = client ? API_ENDPOINTS.tradeWatcherIngestUrl(client.token) : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied('Feed URL copied.');
    } catch (_e) {
      setCopied('Couldn’t copy. Select the URL and copy it by hand.');
    }
  };

  const builds = (client?.builds || []).map((b) => (String(b).toLowerCase() === 'csa-alertonly' ? 'Hands-off (alert-only)' : b));

  return (
    <section className="vd-panel vd-ml-widget">
      <div className="vd-ml-widget-header tw-section-head">
        <div>
          <h3>Client feed URL</h3>
          <p className="tw-muted tw-section-sub">
            Setting up a hands-off client? Look them up by the email they use for RiskLo, copy their feed URL, and paste it
            into the alert-only add-on on their computer. Their accounts then show on the board under their name.
            Trade Watcher is part of RiskLo Pro, so the client needs a Pro subscription.
          </p>
        </div>
      </div>

      <form className="cfl-form" onSubmit={lookUp}>
        <input
          className="tw-input cfl-email"
          type="email"
          placeholder="client@email.com"
          aria-label="Client email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); setNotPro(''); }}
          autoComplete="off"
        />
        <button type="submit" className="vd-btn" disabled={busy}>{busy ? 'Looking up…' : 'Look up'}</button>
      </form>
      {error ? <p className="tw-error" role="alert">{error}</p> : null}
      {notPro ? <p className="cfl-notpro" role="status">{notPro}</p> : null}

      {client ? (
        <div className="cfl-result">
          <div className="cfl-who">
            <strong>{client.name || client.email}</strong>
            {client.name ? <span className="tw-muted"> · {client.email}</span> : null}
          </div>
          <div className="cfl-url-row">
            <input className="tw-input cfl-url" readOnly value={feedUrl} aria-label="Client feed URL" onFocus={(e) => e.target.select()} />
            <button type="button" className="vd-btn" onClick={copy}>Copy URL</button>
          </div>
          {copied ? <p className="tw-muted cfl-copied" role="status">{copied}</p> : null}
          <p className="tw-muted cfl-facts">
            {client.lastPayloadAt
              ? `${client.accounts} account${client.accounts === 1 ? '' : 's'} on the board`
                + `${client.machines.length ? ` from ${client.machines.join(', ')}` : ''}`
                + `${builds.length ? ` · build: ${builds.join(', ')}` : ''}.`
              : 'Nothing has been sent on this URL yet.'}
          </p>
          <ol className="cfl-steps">
            <li>
              Download the{' '}
              <a href={`${API_ENDPOINTS.tradeWatcherAddonDownload}?package=csa`} download>alert-only add-on</a>
              {' '}and import it on the client’s NinjaTrader (Tools → Import → NinjaScript Add-On), then restart.
            </li>
            <li>In the RiskLo Tally window, paste this URL, enter the client’s name, and click Connect.</li>
            <li>Assign their CSA on the CSAs tab so alerts reach the right person.</li>
          </ol>
          <p className="tw-muted cfl-note">Treat this URL like a password: anyone with it can post to this client’s board.</p>
        </div>
      ) : null}
    </section>
  );
}
